import { createUseStyles } from 'react-jss';
import deltaBusinessTravelerCover from '../../resources/images/delta-business-traveler.jpg';
import deltaWarningIcon from '../../resources/images/delta-warning-icon.svg';
import { colors } from "../../constants/colors";
import { breakpoints } from "../../constants/breakpoints";

const styles = createUseStyles(theme => ({
  dlProgramContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.gray600,
    background: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(20,32,58,1) 100%)',
    minHeight: '100vh',
    fontFamily: 'Whitney, sans-serif',
    fontWeight: '600',
    paddingBottom: '100px',
    [`@media (max-width: ${breakpoints.md}px)`]: {
      paddingBottom: '20px',
    },
  },

  // Full-width top bar
  topBar: {
    width: '100%',
    backgroundColor: colors.black,
    padding: '16px 0',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 10,
    [`@media (max-width: ${breakpoints.md}px)`]: {
      paddingLeft: "18px",
      paddingRight: "18px",
      position: "inherit"
    },
  },

  // Content wrapper for topBar
  contentWrapper: {
    maxWidth: '700px',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  businessText: {
    color: colors.gray600,
    fontSize: '32px',
    width: '125px'
  },

  deltaLogo: {
    width: '120px',
  },

  topSection: {
    width: '100vw',
    height: '38vh',
    background: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${deltaBusinessTravelerCover}) no-repeat center center`,
    backgroundSize: 'cover',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: colors.gray600,
    fontFamily: 'Whitney, sans-serif',
    '& h1, & p': {
      width: '700px',
      textAlign: 'left',
    },
    '& h1': {
      fontSize: '40px',
      marginBottom: '8px',
      textTransform: 'uppercase',
      fontWeight: '500',
    },
    '& p': {
      fontSize: '20px',
      lineHeight: '1.5',
      fontWeight: '500',
      marginTop: '8px',
    },
    [`@media (max-width: ${breakpoints.md}px)`]: {
      '& h1, & p': {
        width: 'auto',
        textAlign: 'center',
      },
    },
  },

  // Main content
  mainContent: {
    marginTop: '80px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [`@media (max-width: ${breakpoints.md}px)`]: {
      marginTop: 0
    },
  },

  formSection: {
    maxWidth: '700px',
    textAlign: 'left',
    marginTop: '50px',
    '& p': {
      fontSize: '12px',
      marginBottom: '24px',
      fontWeight: '400',
      '& a': {
        color: colors.gray600,
        fontWeight: "600"
      },
    },
    [`@media (max-width: ${breakpoints.md}px)`]: {
      maxWidth: "none",
      padding: "16px",
      marginTop: '15px',
    },
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [`@media (max-width: ${breakpoints.md}px)`]: {
      display: "grid",
      textAlign: "center",
      marginTop: "25px"
    },
  },
  
  formGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
    width: '100%',
    maxWidth: '700px',
    position: 'relative',
    gap: "8px",
    [`@media (max-width: ${breakpoints.md}px)`]: {
      display: "grid",
      maxWidth: "none",
      justifyContent: "normal",
    },
  },
  
  inputGroup: {
    position: 'relative',
    margin: '24px 0',
  },
  
  input: {
    flex: 1,
    padding: '20px 0 6px',
    fontSize: '16px',
    width: '100%',
    border: 'none',
    fontWeight: "300",
    borderBottom: `2px solid ${colors.darkGrey}`,
    backgroundColor: 'transparent',
    color: colors.white,
    transition: 'border-color 0.2s ease-in-out, transform 0.2s ease-in-out',
    paddingLeft: "10px",
    '&:disabled': {
      paddingLeft: "10px",
      color: `${colors.disabledBlack} !important`,
      border: "none !important",
      borderBottom: `2px solid ${colors.darkGrey} !important`,
      backgroundColor: "transparent !important"
    },
    '&::placeholder': {
      color: colors.inactiveGrey, // Placeholder text color
      fontSize: '14px', // Default placeholder size
    },
    '&:focus': {
      borderBottom: `2px solid ${colors.error400}`, // Red border for focus
      outline: 'none',
    },
    '&:focus::placeholder': {
      fontSize: '12px', // Shrinks placeholder on focus
      fontWeight: 'bold', // Makes placeholder bold
      transform: 'translateY(-20px) scale(1)', // Moves placeholder slightly higher
      color: colors.white,
      transition: 'transform 0.2s ease-in-out, font-size 0.2s ease-in-out',
    },
    '&:-webkit-autofill': {
      backgroundColor: 'transparent !important',
      transition: 'background-color 5000s ease-in-out 0s',
      '-webkit-text-fill-color': `${colors.white} !important`
    },
    [`@media (max-width: ${breakpoints.md}px)`]: {
      margin: 0
    },
  },

  registerButtonContainer: {
    [`@media (max-width: ${breakpoints.md}px)`]: {
      display: "flex",
      justifyContent: "center"
    },
  },

  error: {
    display: 'flex',
    top: '-33px',
    right: '-27px',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: '12px',
    color: colors.error400,
    position: 'absolute',
    zIndex: 10,
    backgroundColor: colors.white,
    color: colors.darkBlue3,
    width: 'fit-content',
    height: '32px',
    padding: '10px 15px 10px 15px',

    '&:before': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      content: `url(${deltaWarningIcon})`, // Add an icon before the message
      marginRight: '4px',
      display: 'inline-block',
      width: "20px"
    },
    [`@media (max-width: ${breakpoints.md}px)`]: {
      right: "auto",
      top: "160px"
    },
  },

  formErrorTooltip: {
    position: 'absolute',
    background: colors.error400,
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    bottom: '-35px', // Adjust based on field spacing
    left: '50%',
    transform: 'translateX(-50%)',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.3s, visibility 0.3s',
    zIndex: 1,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '100%',
      left: '50%',
      borderWidth: '5px',
      borderStyle: 'solid',
      borderColor: `${colors.error400} transparent transparent transparent`,
      transform: 'translateX(-50%)',
    },
  },
  formErrorContainerHover: {
    position: 'relative',
    '&:hover $formErrorTooltip': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  
  forgotSkyMilesText: {
    fontSize: '16px',
    fontWeight: '600',
    textAlign: 'left',
    width: '700px',
    margin: '20px 0',
    '& a': {
      color: colors.gray600,
    },
    [`@media (max-width: ${breakpoints.md}px)`]: {
      width: "auto"
    },
  },

  checkboxes: {
    width: '700px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: '24px',
    fontWeight: '300',
    '& label': {
      display: 'flex',
      flexDirection: 'row',
      marginBottom: '4px',
      fontSize: '12px',
      lineHeight: '20px',
    },
    '& input': {
      marginRight: '8px', 
    },
    '& a': {
      fontWeight: '500',
      color: colors.gray600,
      paddingLeft: '5px',
    },
    [`@media (max-width: ${breakpoints.md}px)`]: {
      width: "auto"
    },
  },

  submitButton: {
    display: 'flex',
    justifyContent: 'center',
    padding: '12px 48px',
    fontSize: '12px',
    backgroundColor: colors.deltaRed,
    color: colors.white,
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.deltaRedHover,
    },
    '&:active': {
      backgroundColor: colors.deltaRed2,
    },
    '&:focus': {
      boxShadow: `0 0 0 2px lighten(${colors.deltaRed}, 20)`
    },
    '&:disabled': {
      backgroundColor: colors.inactiveGrey,
      color: colors.deltaDarkBlue,
    }
  },
  errorSkyMilesText: {
    fontSize: '14px',
    fontWeight: '400',
    textAlign: 'left',
    width: '700px',
    margin: '2px 0',
    marginLeft:"8px",
    color: colors.error400,
    [`@media (max-width: ${breakpoints.md}px)`]: {
      width: "auto"
    },
  },
}));

export default styles;